import { Edit, Trash2 } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Fund } from '@/types/fund';
import type {
  InvestorDetail,
  InvestorFundRelationship,
} from '@/types/investor';
import { investorService } from '@/services/investorService';
import { formatCurrencyNoDecimals, formatDate } from '@/utils/formatters';

import { getRelationshipForFund } from './investorTransferUtils';

interface FundInvestorsTableProps {
  fund: Fund;
  investors: InvestorDetail[];
  loading: boolean;
  onInvestorClick: (investor: InvestorDetail) => void;
  onEditCommitment: (relationship: InvestorFundRelationship) => void;
  onRemoveInvestor: (investor: InvestorDetail) => void;
}

function relationshipStatusVariant(
  status: string | undefined
): 'default' | 'destructive' | 'secondary' {
  if (status === 'ACTIVE') return 'default';
  if (status === 'EXITED') return 'destructive';
  return 'secondary';
}

export function FundInvestorsTable({
  fund,
  investors,
  loading,
  onInvestorClick,
  onEditCommitment,
  onRemoveInvestor,
}: FundInvestorsTableProps) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Investor</TableHead>
            <TableHead>Country</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>KYC</TableHead>
            <TableHead className="text-right">Commitment</TableHead>
            <TableHead>Commitment Date</TableHead>
            <TableHead>Custom Terms</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {investors.map((investor) => {
            const relationship = getRelationshipForFund(investor, fund.id);
            const statusVariant = relationshipStatusVariant(
              relationship?.status
            );

            return (
              <TableRow
                key={investor.id}
                className="cursor-pointer"
                onClick={() => onInvestorClick(investor)}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    {investor.investing_fund_id ? (
                      <Link
                        to={`/app/funds/${investor.investing_fund_id}`}
                        className="font-medium hover:underline"
                        data-testid="investor-name"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {investor.name}
                      </Link>
                    ) : (
                      <span className="font-medium" data-testid="investor-name">
                        {investor.name}
                      </span>
                    )}
                    {investor.investing_fund_id && (
                      <Badge variant="outline" className="text-xs">
                        Fund
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {investorService.getInvestorTypeLabel(
                      investor.investor_type
                    )}
                  </div>
                </TableCell>

                <TableCell>
                  <span className="text-sm">
                    {investor.country || '\u2014'}
                  </span>
                </TableCell>

                <TableCell>
                  <Badge variant={statusVariant} className="text-xs">
                    {relationship?.status
                      ? relationship.status.charAt(0) +
                        relationship.status.slice(1).toLowerCase()
                      : '\u2014'}
                  </Badge>
                </TableCell>

                <TableCell>
                  <Badge
                    className={investorService.getKycStatusColor(
                      investor.kyc_status
                    )}
                  >
                    {investorService.getKycStatusLabel(investor.kyc_status)}
                  </Badge>
                </TableCell>

                <TableCell className="text-right">
                  <span className="font-medium">
                    {relationship
                      ? formatCurrencyNoDecimals(
                          relationship.commitment_amount,
                          relationship.commitment_currency
                        )
                      : '\u2014'}
                  </span>
                </TableCell>

                <TableCell>
                  <span className="text-sm">
                    {relationship
                      ? formatDate(relationship.commitment_date) || '\u2014'
                      : '\u2014'}
                  </span>
                </TableCell>

                <TableCell>
                  <span className="text-sm">
                    {relationship?.has_custom_terms ? 'Yes' : 'No'}
                  </span>
                </TableCell>

                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (relationship) {
                          onEditCommitment(relationship);
                        }
                      }}
                      disabled={loading || !relationship}
                      aria-label={`Edit commitment for ${investor.name}`}
                      title="Edit commitment"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        onRemoveInvestor(investor);
                      }}
                      disabled={loading}
                      aria-label={`Remove ${investor.name} from fund`}
                      title="Remove investor"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
